import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { kebabCase } from 'lodash'
import { omit, isEmpty } from 'lodash'
import { withRouter } from 'next/router'
import { MdClose } from 'react-icons/md'
import { useMemo } from 'react'

import ShadowDivisor from 'components/Atoms/ShadowDivisor'
import Text from 'components/Atoms/Text'
import { FilterBy } from 'components/Molecules/Events'
import { getAppRoute } from 'helpers/application'
import { getEventsQueryString } from 'models/events'
import { useTranslation } from 'i18n-web/i18next'

const Component = styled.div`
  display: flex;
  flex: 1;
  margin-top: 25px;
  height: 27px;
  position: relative;
  justify-content: space-between;
  @media (min-width: ${props => props.theme.flexboxgrid.breakpoints.sm}rem) {
    flex: 0 65%;
    margin-top: 0;
    margin-bottom: 0;
    justify-content: flex-end;
  }
  @media (min-width: ${props => props.theme.flexboxgrid.breakpoints.md}rem) {
    flex: 0 67%;
  }
`

const CloseIcon = styled(MdClose)`
  display: inline-flex;
  border: 1px solid ${props => props.theme.palette.grayDark};
  border-radius: 50%;
  margin-left: 5px;
  padding: 1px;
  font-size: ${props => props.theme.font.size.mobile.small};
`

const Filter = styled(FilterBy)`
  flex: 0 30%;
  @media (min-width: ${props => props.theme.flexboxgrid.breakpoints.sm}rem) {
    margin-left: 20px;
  }
`

const MobileClearFilter = styled(Text)`
  position: absolute;
  right: 0;
  top: -42px;
  display: flex;
  align-items: center;
  @media (min-width: ${props => props.theme.flexboxgrid.breakpoints.sm}rem) {
    display: none;
  }
`

const Filters = ({ router, filters, setIsLoadingEvents, setCurrentPage }) => {
  const { t } = useTranslation()
  const [queryObject, setQueryObject] = useState()
  const [resetFilters, setResetFilters] = useState(false)

  const onClear = type => {
    setCurrentPage(1)
    if (type === 'eventType') {
      setQueryObject({ ...omit(queryObject, ['eventType']) })
    } else if (type === 'location') {
      setQueryObject({ ...omit(queryObject, ['latitude', 'longitude']) })
    } else if (type === 'date') {
      setQueryObject({ ...omit(queryObject, ['startDate']) })
    } else {
      setQueryObject({})
    }
  }

  const showMobileClearFilter = useMemo(() => {
    const { query } = router

    return query?.search || !isEmpty(queryObject)
  }, [router, queryObject])

  const onChange = item => {
    setCurrentPage(1)
    if (item.type === 'eventType') {
      setQueryObject({ ...queryObject, eventType: item.value })
    } else if (item.type === 'location') {
      setQueryObject({
        ...queryObject,
        latitude: item.coordinates.latitude,
        longitude: item.coordinates.longitude,
      })
    } else if (item.type === 'date') {
      if (item?.value) {
        setQueryObject({
          ...queryObject,
          startDate: item.value.format('YYYY-MM-DD'),
        })
      }
    }
  }

  useEffect(() => {
    if (resetFilters) {
      setResetFilters(false)
    }
  }, [resetFilters])

  useEffect(() => {
    if (queryObject) {
      setIsLoadingEvents(true)
      const { application } = router.query

      const query = {
        ...(!isEmpty(queryObject) && {
          search: getEventsQueryString(queryObject),
        }),
      }

      router.push(
        {
          pathname: `${getAppRoute(application, 'as')}/events`,
          query: query,
        },
        {
          pathname: `${getAppRoute(application, 'url')}/events`,
          query: query,
        }
      )
    }
  }, [queryObject])

  return (
    <Component>
      {showMobileClearFilter && (
        <MobileClearFilter
          onClick={() => {
            onClear()
            setResetFilters(true)
          }}
          className="tiny">
          {t('events.clearFilters')}
          <CloseIcon />
        </MobileClearFilter>
      )}
      {filters?.eventType?.enabled && (
        <Filter
          label={t('events.filters.byType')}
          data={filters.eventType.options.map(item => ({
            label: item?.name?.plural,
            value: item?.code,
          }))}
          onClear={onClear}
          resetFilters={resetFilters}
          onChange={onChange}
          type="eventType"
        />
      )}
      {filters?.location?.enabled && (
        <Filter
          label={t('events.filters.byLocation')}
          data={filters.location.options.map(item => ({
            label: item?.name,
            value: kebabCase(item?.name),
            countryCode: item?.countryCode,
            coordinates: item?.coordinates,
          }))}
          setIsLoadingEvents={setIsLoadingEvents}
          onClear={onClear}
          resetFilters={resetFilters}
          onChange={onChange}
          type="location"
        />
      )}
      {filters?.startDate?.enabled && (
        <Filter
          className="noBorder"
          label={t('events.filters.byDate')}
          onChange={onChange}
          resetFilters={resetFilters}
          onClear={onClear}
          type="date"
        />
      )}
      <ShadowDivisor />
    </Component>
  )
}

Filters.propTypes = {
  filters: PropTypes.object,
  router: PropTypes.object,
  setIsLoadingEvents: PropTypes.func,
  setCurrentPage: PropTypes.func,
}

export default withRouter(Filters)
